import React from "react";

import { ITeacherSurvey, ISubmitTeacherSurvey } from "../../../../state";
import { ReactComponent as CloseIcon } from "../../../../assets/svg/close.svg";
import { ReactComponent as RightIcon } from "../../../../assets/svg/arrow_right.svg";
import { ReactComponent as LeftIcon } from "../../../../assets/svg/arrow_left.svg";
import {
  TEACHER_SURVEY_MODAL_PAGES,
  TeacherSurveyModalPage,
} from "../../../../constants/teaching_hint";
import {
  formatNameWithPassedStatus,
  isFiscalMonthPassed,
} from "../../../../libs/Util";

import styles from "./Confirmation.module.scss";

type Props = {
  formData: ISubmitTeacherSurvey | null;
  onClose: () => void;
  setModal: React.Dispatch<React.SetStateAction<TeacherSurveyModalPage>>;
  calling: boolean;
  onUpdateTeacherSurvey: (teacherSurvey: ISubmitTeacherSurvey) => void;
  onCreateTeacherSurvey: (teacherSurvey: ISubmitTeacherSurvey) => void;
  teacherSurvey: ITeacherSurvey | null;
};

const Confirmation = (props: Props) => {
  const getISODate = React.useCallback((input: string): string => {
    const digitsOnly = input.replace(/\//g, "");
    if (digitsOnly.length !== 8) return "";
    return `${digitsOnly.slice(0, 4)}-${digitsOnly.slice(
      4,
      6,
    )}-${digitsOnly.slice(6, 8)}`;
  }, []);

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div className={styles.leftPortion}>
          <h1 className={styles.headerText}>入力した情報を確認してね！</h1>
        </div>
        <button className={styles.closeButton}>
          <CloseIcon onClick={props.onClose} />
        </button>
      </div>
      <div className={styles.content}>
        <p className={styles.requiredField}>*必須項目</p>
        <div className={styles.answerboxContainer}>
          <div className={styles.answerBox}>
            <div className={styles.header}>1.生年月日</div>
            <div className={styles.description}>
              生年月日を西暦で教えてください。(半角数字)
            </div>

            {props.formData?.birth_date?.trim() ? (
              <p className={styles.answer}>{props.formData.birth_date}</p>
            ) : (
              <div className={styles.notAnswered}>未回答</div>
            )}
          </div>
          <div className={styles.answerBox}>
            <div className={styles.header}>2.教員歴</div>
            <div className={styles.description}>教員歴を教えてください。</div>

            {props.formData?.years_of_teaching !== null &&
            props.formData?.years_of_teaching !== undefined ? (
              <p className={styles.answer}>
                {props.formData.years_of_teaching}年
              </p>
            ) : (
              <div className={styles.notAnswered}>未回答</div>
            )}
          </div>
          <div className={styles.answerBox}>
            <div className={styles.header}>3.担当教科</div>
            <div className={styles.description}>
              ご担当されている教科を教えてください。
            </div>
            <div className={`${styles.description} pb-2`}>
              ※ご担当がない場合は未入力で問題ありません。
            </div>
            {props.formData?.subjects &&
            props.formData.subjects.filter(s => s.trim() !== "").length > 0 ? (
              props.formData.subjects
                .filter(subject => subject.trim() !== "")
                .map((subject, index) => (
                  <p className={styles.answer} key={`${subject} - ${index}`}>
                    {subject}
                  </p>
                ))
            ) : (
              <div className={styles.notAnswered}>未回答</div>
            )}
          </div>
          <div className={styles.answerBox}>
            <div className={styles.header}>
              4.先生や生徒の興味・関心<span className={styles.required}>*</span>
            </div>
            <div className={styles.description}>
              先生や生徒の「好きなこと」「興味・関心があること」「生徒たちがよく話題にすること」「最近流行っていること」などをなるべくたくさん教えてください。
            </div>
            <p className={styles.answer}>{props.formData?.interests}</p>
          </div>
          <div className={styles.answerBox}>
            <div className={styles.header}>
              5.生徒に感じる課題<span className={styles.required}>*</span>
            </div>
            <div className={styles.description}>
              先生が受け持つクラスの生徒に対して感じている課題を教えてください。
            </div>
            <p className={styles.answer}>{props.formData?.class_challenges}</p>
          </div>
          <div className={styles.answerBox}>
            <div className={styles.header}>
              6.理想の生徒像<span className={styles.required}>*</span>
            </div>
            <div className={styles.description}>
              ENAGEEDを通じて、先生が受け持つクラスの生徒にどのようになってほしいかを教えてください。
            </div>
            <p className={styles.answer}>
              {props.formData?.desired_student_outcomes}
            </p>
          </div>
          <div className={styles.answerBox}>
            <div className={styles.header}>7.今年度のイベント</div>
            <div className={styles.description}>
              クラスや学校で予定しているイベントや行事を教えてください。
              <br />
              ※入力できるイベントは3つまでです。いつでも書き換え可能です。
            </div>

            {props.formData?.school_events?.some(
              event =>
                event.name.trim() !== "" &&
                event.description.trim() !== "" &&
                event.month !== null &&
                event.month !== undefined,
            ) ? (
              <div className={styles.eventTable}>
                <div className={styles.eventTableHeader}>
                  <span>イベント名</span>
                  <span>概要</span>
                  <span>イベント時期</span>
                </div>

                {props.formData.school_events
                  .filter(
                    event =>
                      event.name.trim() !== "" &&
                      event.description.trim() !== "" &&
                      event.month !== null &&
                      event.month !== undefined,
                  )
                  .map((event, index) => (
                    <div
                      className={styles.eventTableRow}
                      key={`${event.name}-${index}`}
                      style={{
                        color: isFiscalMonthPassed(event.month)
                          ? "#999"
                          : "inherit",
                      }}
                    >
                      <span>{formatNameWithPassedStatus(event)}</span>
                      <span>{event.description}</span>
                      <span>{event.month}月</span>
                    </div>
                  ))}
              </div>
            ) : (
              <div className={styles.notAnswered}>未回答</div>
            )}

            {props.formData?.school_events?.some(
              event =>
                event.name.trim() !== "" &&
                event.description.trim() !== "" &&
                event.month !== null &&
                event.month !== undefined,
            ) ? (
              props.formData.school_events
                .filter(
                  event =>
                    event.name.trim() !== "" &&
                    event.description.trim() !== "" &&
                    event.month !== null &&
                    event.month !== undefined,
                )
                .map((event, index) => (
                  <div
                    className={styles.tabletEvent}
                    key={`${event.name}-${index}`}
                  >
                    <div className={styles.listRow}>
                      <p className={styles.listHeader}>イベント名</p>
                      <p
                        className={styles.listBody}
                        style={{
                          color: "inherit",
                        }}
                      >
                        {event.name}
                      </p>
                    </div>
                    <div className={styles.listRow}>
                      <p className={styles.listHeader}>概要</p>
                      <p
                        className={styles.listBody}
                        style={{
                          color: "inherit",
                        }}
                      >
                        {event.description}
                      </p>
                    </div>
                    <div className={styles.listRow}>
                      <p className={styles.listHeader}>イベント時期</p>
                      <p
                        className={styles.listBody}
                        style={{
                          color: "inherit",
                        }}
                      >
                        {event.month}月
                      </p>
                    </div>
                  </div>
                ))
            ) : (
              <div className={styles.notAnsweredTablet}>未回答</div>
            )}
          </div>
        </div>
        <div className={styles.buttonContainer}>
          <button
            className={styles.backButton}
            onClick={() => props.setModal(TEACHER_SURVEY_MODAL_PAGES.FORM)}
          >
            <LeftIcon />
            戻る
          </button>
          <button
            className={styles.submitButton}
            disabled={props.calling}
            onClick={() => {
              const filteredSchoolEvents =
                props.formData?.school_events?.filter(event => {
                  const nameEmpty = event.name.trim() === "";
                  const descriptionEmpty = event.description.trim() === "";
                  return !(nameEmpty && descriptionEmpty);
                }) ?? [];

              const filteredSubjects =
                props.formData?.subjects?.filter(
                  subject => subject.trim() !== "",
                ) ?? [];

              const payload = {
                birth_date: getISODate(props.formData?.birth_date ?? ""),
                years_of_teaching: props.formData?.years_of_teaching ?? null,
                interests: props.formData?.interests ?? "",
                class_challenges: props.formData?.class_challenges ?? "",
                desired_student_outcomes:
                  props.formData?.desired_student_outcomes ?? "",
                subjects: filteredSubjects,
                school_events: filteredSchoolEvents,
              };
              if (props.teacherSurvey) {
                props.onUpdateTeacherSurvey(payload);
              } else {
                props.onCreateTeacherSurvey(payload);
              }

              props.setModal(TEACHER_SURVEY_MODAL_PAGES.SUCCESS);
            }}
          >
            情報を送信する <RightIcon />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Confirmation;
